import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { getDisplayError } from '@utils/get-display-error';
import { Breadcrumb } from 'components/Breadcrumb';
import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { InputField } from 'components/input/InputField';
import { PageHeader } from 'components/PageHeader';
import { PageHeading } from 'components/PageHeading';
import { useAuth } from 'contexts/auth-context';
import { InternalUser, UserComboboxField } from 'src/app/user/components/UserCombobox';
import { CheckboxField } from 'components/checkbox/CheckboxField';
import { SimpleSelectField } from 'components/select/SimpleSelectField';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';
import { INTERNAL_TRAILER_TYPE_OPTIONS } from '../constants';
import { createTrailer, type CreateTrailerInput } from '../models';

const createTrailerSchema = Yup.object().shape({
  name: Yup.string().min(1, 'Vereist').required('Vereist'),
  licensePlate: Yup.string().min(1, 'Vereist').required('Vereist'),
  brand: Yup.string().min(1, 'Vereist').required('Vereist'),
  serialNumber: Yup.string().min(1, 'Vereist').required('Vereist'),
  registrationDate: Yup.string().required('Vereist'),
  insuranceExpirationDate: Yup.string().when('isRental', {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.required('Vereist'),
  }),
  inspectionExpirationDate: Yup.string().when('isRental', {
    is: true,
    then: (schema) => schema,
    otherwise: (schema) => schema.required('Vereist'),
  }),
  trailerType: Yup.object().required('Vereist'),
});

export const CreateTrailerPage = () => {
  const navigate = useNavigate();
  const { me } = useAuth();

  const initialValues = {
    name: '',
    licensePlate: '',
    brand: '',
    serialNumber: '',
    registrationDate: '',
    insuranceExpirationDate: '',
    inspectionExpirationDate: '',
    responsible: me as InternalUser,
    trailerType: INTERNAL_TRAILER_TYPE_OPTIONS[0],
    isRental: false,
    rentalCompany: '',
  };

  return (
    <>
      <PageHeader title="Nieuwe trailer" />

      <PageHeading
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Trailers',
              to: '/internal/trailers',
            }}
            currentItem="Nieuwe trailer"
          />
        }
      />

      <div className="px-4">
        <Formik
          initialValues={initialValues}
          validationSchema={createTrailerSchema}
          onSubmit={async (values) => {
            try {
              const inputData: CreateTrailerInput = {
                responsibleId: values.responsible?.id,
                name: values.name,
                licensePlate: values.licensePlate,
                brand: values.brand,
                serialNumber: values.serialNumber,
                registrationDate: values.registrationDate,
                insuranceExpirationDate: values.insuranceExpirationDate,
                inspectionExpirationDate: values.inspectionExpirationDate,
                trailerType: values.trailerType.key,
                isRental: values.isRental,
                rentalCompany: values.rentalCompany || undefined,
              };

              const result = await createTrailer(inputData);
              trackEvent('trailer_created', {
                trailer_id: result.id,
                is_rental: inputData.isRental,
              });
              toast.success('Trailer aangemaakt');
              navigate('..');
            } catch (err: any) {
              captureException(err);
              toast.error('Kon trailer niet aanmaken: ' + getDisplayError(err));
            }
          }}
        >
          {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
            <form onSubmit={handleSubmit} className="grid gap-4">
              <Card title="Identificatie">
                <UserComboboxField labelText="Verantwoordelijke" name="responsible" />
                <InputField labelText="ID Code" type="text" name="name" />
                <InputField labelText="Nummerplaat" type="text" name="licensePlate" />
                <InputField labelText="Merk" type="text" name="brand" />
                <InputField labelText="Serienummer" type="text" name="serialNumber" />
                <InputField labelText="Datum eerste registratie" type="date" name="registrationDate" />
                <SimpleSelectField labelText="Type" name="trailerType" items={[...INTERNAL_TRAILER_TYPE_OPTIONS]} />
              </Card>

              <Card title="Huurtrailer">
                <CheckboxField labelText="Dit is een huurtrailer" name="isRental" />
                {values.isRental && <InputField labelText="Verhuurmaatschappij" type="text" name="rentalCompany" />}
              </Card>

              <Card title="Vervaldagen">
                <InputField labelText="Vervaldag verzekering" type="date" name="insuranceExpirationDate" />
                <InputField labelText="Vervaldag keuring" type="date" name="inspectionExpirationDate" />
              </Card>

              <div>
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  iconLeft={<PlusIcon className="button-icon" />}
                >
                  Maak trailer aan
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
};
